import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Modal, Toast, useToast } from '../components/ui';
import { ConfirmarSenhaTemporaria, SenhaGeradaModal, type SenhaGerada } from '../components/SenhaTemporaria';
import { formatarWhatsapp, num, relativo } from '../format';

interface UsuarioLinha {
  id: string; nome: string; email: string; perfil: 'ADMIN' | 'VENDEDOR';
  status: 'ATIVO' | 'INATIVO'; ultimoAcesso: string | null; clientes: number; whatsapp: string | null;
}

const PERFIL_LABEL = { ADMIN: 'Administrador', VENDEDOR: 'Vendedor' } as const;

export function UsuariosPage() {
  const { me, pode } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [novo, setNovo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [editarWhats, setEditarWhats] = useState<UsuarioLinha | null>(null);
  const [senhaPara, setSenhaPara] = useState<UsuarioLinha | null>(null);
  const [senhaGerada, setSenhaGerada] = useState<SenhaGerada | null>(null);
  const q = useQuery({ queryKey: ['usuarios'], queryFn: () => api<UsuarioLinha[]>('/usuarios') });

  if (!pode('USUARIOS_GERENCIAR')) return <Navigate to="/" replace />;

  const max = me?.empresa.plano?.maxUsuarios ?? null;
  const ativos = q.data?.filter((u) => u.status === 'ATIVO').length ?? 0;
  const noLimite = max != null && ativos >= max;

  const alterarStatus = async (u: UsuarioLinha, status: UsuarioLinha['status']) => {
    setErro(null);
    setOcupado(u.id);
    try {
      await api(`/usuarios/${u.id}`, { method: 'PATCH', body: { status } });
      await qc.invalidateQueries({ queryKey: ['usuarios'] });
      toast.show(status === 'INATIVO' ? `${u.nome} foi desativado(a).` : `${u.nome} foi reativado(a).`);
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setOcupado(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Usuários</h1>
          <span className="num muted small">
            {q.data ? `${num(ativos)}${max != null ? ` de ${num(max)}` : ''} ativos no plano ${me?.empresa.plano?.nome ?? ''}` : ' '}
          </span>
        </div>
        <div className="actions">
          <button className="btn primary" disabled={noLimite} title={noLimite ? 'Limite do plano atingido' : undefined} onClick={() => setNovo(true)}>
            <Icon name="mais" size={15} />Novo usuário
          </button>
        </div>
      </div>

      {noLimite && (
        <div className="alert warn"><Icon name="alerta" /><span>Você atingiu o limite de {max} usuários ativos do seu plano. Desative alguém ou fale com o suporte para mudar de plano.</span></div>
      )}
      <Erro erro={erro ?? (q.error ? mensagemErro(q.error) : null)} />

      <div className="card flush">
        {q.isLoading ? <Carregando /> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Usuário</th><th>Perfil</th><th>WhatsApp</th><th>Status</th><th>Clientes</th><th>Último acesso</th><th /></tr></thead>
              <tbody>
                {q.data!.map((u) => (
                  <tr key={u.id}>
                    <td><span style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 500 }}>{u.nome}{u.id === me?.usuario.id ? ' (você)' : ''}</span>
                      <span className="small faint">{u.email}</span></span></td>
                    <td>{PERFIL_LABEL[u.perfil]}</td>
                    <td>
                      <button type="button" className="link-btn num" onClick={() => setEditarWhats(u)} title="Usado no botão “Falar no WhatsApp” dos e-mails">
                        {u.whatsapp ? formatarWhatsapp(u.whatsapp) : 'Cadastrar'}
                      </button>
                    </td>
                    <td>{u.status === 'ATIVO' ? <span className="badge green">Ativo</span> : <span className="badge">Inativo</span>}</td>
                    <td className="num">{num(u.clientes)}</td>
                    <td className="num">{u.ultimoAcesso ? relativo(u.ultimoAcesso) : 'Nunca'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {u.id !== me?.usuario.id && (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          {u.status === 'ATIVO' && <button className="btn sm" disabled={ocupado === u.id} onClick={() => setSenhaPara(u)}>Redefinir senha</button>}
                          {u.status === 'ATIVO'
                            ? <button className="btn sm danger" disabled={ocupado === u.id} onClick={() => alterarStatus(u, 'INATIVO')}>Desativar</button>
                            : <button className="btn sm" disabled={ocupado === u.id || noLimite} onClick={() => alterarStatus(u, 'ATIVO')}>Reativar</button>}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="small faint" style={{ margin: 0 }}>
        Desativar corta o acesso na hora e mantém o histórico. Os clientes continuam atribuídos ao usuário até serem redistribuídos. Esqueceu a senha? Use Redefinir senha: o sistema gera uma temporária e a pessoa cria a dela no primeiro acesso.
      </p>

      {senhaPara && (
        <ConfirmarSenhaTemporaria nome={senhaPara.nome} onClose={() => setSenhaPara(null)}
          onConfirmar={async () => {
            const r = await api<SenhaGerada>(`/usuarios/${senhaPara.id}/senha-temporaria`, { body: {} });
            setSenhaPara(null);
            setSenhaGerada(r);
          }} />
      )}
      {senhaGerada && <SenhaGeradaModal dados={senhaGerada} onClose={() => setSenhaGerada(null)} />}
      {editarWhats && (
        <ModalWhatsappVendedor usuario={editarWhats} onClose={() => setEditarWhats(null)}
          onSalvo={async () => { setEditarWhats(null); await qc.invalidateQueries({ queryKey: ['usuarios'] }); toast.show('WhatsApp atualizado.'); }} />
      )}
      {novo && (
        <ModalNovoUsuario
          onClose={() => setNovo(false)}
          onCriado={async (nome) => { setNovo(false); await qc.invalidateQueries({ queryKey: ['usuarios'] }); toast.show(`${nome} foi cadastrado(a). Envie o e-mail e a senha para a pessoa entrar.`); }}
        />
      )}
      <Toast msg={toast.msg} onDone={toast.clear} />
    </>
  );
}

function ModalNovoUsuario({ onClose, onCriado }: { onClose: () => void; onCriado: (nome: string) => void }) {
  const [f, setF] = useState({ nome: '', email: '', perfil: 'VENDEDOR' as 'ADMIN' | 'VENDEDOR', senha: '', confirmar: '' });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const salvar = async (e: FormEvent) => {
    e.preventDefault();
    setErro(null);
    if (f.senha !== f.confirmar) return setErro('As senhas não conferem.');
    setSalvando(true);
    try {
      await api('/usuarios', { body: { nome: f.nome, email: f.email, perfil: f.perfil, senha: f.senha } });
      onCriado(f.nome);
    } catch (err) {
      setErro(mensagemErro(err));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal titulo="Novo usuário" onClose={onClose}>
      <h2 style={{ fontSize: 18 }}>Novo usuário</h2>
      <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Erro erro={erro} />
        <label className="field"><span>Nome *</span><input className="input" required minLength={2} value={f.nome} onChange={set('nome')} autoFocus /></label>
        <label className="field"><span>E-mail *</span><input className="input" type="email" required value={f.email} onChange={set('email')} autoComplete="off" /></label>
        <div className="field"><span>Perfil</span>
          <div className="opts">
            {(['VENDEDOR', 'ADMIN'] as const).map((p) => (
              <button type="button" key={p} className={`opt${f.perfil === p ? ' on' : ''}`} aria-pressed={f.perfil === p} onClick={() => setF({ ...f, perfil: p })}>{PERFIL_LABEL[p]}</button>
            ))}
          </div>
          <span className="hint">{f.perfil === 'ADMIN' ? 'Acesso total: usuários, todos os clientes e relatórios.' : 'Vê e trabalha a própria carteira de clientes.'}</span>
        </div>
        <label className="field"><span>Senha inicial *</span><input className="input" type="password" required minLength={10} value={f.senha} onChange={set('senha')} autoComplete="new-password" /><span className="hint">Mínimo de 10 caracteres. Repasse para a pessoa por um canal seguro.</span></label>
        <label className="field"><span>Confirmar senha *</span><input className="input" type="password" required value={f.confirmar} onChange={set('confirmar')} autoComplete="new-password" /></label>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn primary" disabled={salvando}>{salvando ? 'Salvando…' : 'Cadastrar'}</button>
        </div>
      </form>
    </Modal>
  );
}

/** WhatsApp do vendedor nesta empresa (botão "Falar no WhatsApp" dos e-mails, specs/24). */
function ModalWhatsappVendedor({ usuario, onClose, onSalvo }: { usuario: UsuarioLinha; onClose: () => void; onSalvo: () => Promise<void> }) {
  const [v, setV] = useState(usuario.whatsapp ? formatarWhatsapp(usuario.whatsapp) : '');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const salvar = async (e: FormEvent) => {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await api(`/usuarios/${usuario.id}/whatsapp`, { method: 'PUT', body: { whatsapp: v.trim() || null } });
      await onSalvo();
    } catch (err) {
      setErro(mensagemErro(err));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal titulo={`WhatsApp de ${usuario.nome}`} onClose={onClose}>
      <form className="col" style={{ gap: 14 }} onSubmit={salvar}>
        <h2 style={{ fontSize: 18 }}>WhatsApp de {usuario.nome}</h2>
        <Erro erro={erro} />
        <label className="field"><span>WhatsApp comercial</span>
          <input className="input num" inputMode="tel" placeholder="(47) 9 0000-0000" value={v} maxLength={30} autoFocus onChange={(e) => setV(e.target.value)} />
          <span className="hint">Vai no botão “Falar no WhatsApp” dos e-mails dessa pessoa. Deixe vazio para tirar.</span>
        </label>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn primary" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </form>
    </Modal>
  );
}
